import Button from "@ergeon/core-components/exports/Button.ts";

import Logo from "@ergeon/core-components/exports/Logo.ts";

import { Permissions, RequestPermissions } from "@/app/types";
import DevicePermissionChip from "@/app/pages/LobbyPage/DevicePermissionChip";
import LocationPermissionChip from "@/app/pages/LobbyPage/LocationPermissionChip";
import CompassPermissionChip from "@/app/pages/LobbyPage/CompassPermissionChip";
import LobbyVideo from "@/app/pages/LobbyPage/LobbyVideo";
import ConsentNotice from "@/app/pages/LobbyPage/ConsentNotice";
import useLocationPermission from "@/app/hooks/useLocationPermission";
import useCompassPermission from "@/app/hooks/useCompassPermission";

interface ILobbyPageProps {
  startTime: string;
  address: string;
  customerName?: string;
  staffName?: string;
  permissions: Permissions;
  denied: Permissions;
  requestPermissions: RequestPermissions;
  videoStream?: MediaStream;
  onJoin(): void;
  isClient: boolean;
}

const SECTION_LABEL = "paragraph-3 font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400";
const MUTED = "text-gray-500 dark:text-gray-400";
const WARNING = "text-orange-600 dark:text-orange-400 animate-fade-in paragraph-3";

export default function LobbyPage({ startTime, denied, address, customerName, staffName, permissions, requestPermissions, videoStream, onJoin, isClient }: ILobbyPageProps) {
  const joinButtonText = isClient ? "Join & Consent" : "Join call";
  const meetingWith = isClient ? staffName : customerName;

  // Owned here rather than in the chip, because the readiness heading above the
  // button can only claim "ready" if it knows about all three permissions. Only
  // the customer walks the property, so only they are asked for location.
  const location = useLocationPermission(isClient);
  const compass = useCompassPermission(isClient);
  const tracksLocation = isClient && location.state !== "unsupported";
  const tracksHeading = isClient && compass.state !== "unsupported";
  const isReady = permissions.video && permissions.audio && (!tracksLocation || location.state === "granted");

  const joinButton = (
    <Button flavor="primary" isFullWidth onClick={onJoin} size="large">
      {joinButtonText}
    </Button>
  );

  // A customer whose microphone is off can still join — the call is simply
  // one-way until they fix it, and saying so beats a button that only looks
  // broken.
  const microphoneWarning = denied.audio && <p className={WARNING}>Your microphone is off — turn it on so you can be heard.</p>;
  const cameraWarning = denied.video && <p className={WARNING}>Your camera is off — turn it on so you can be seen.</p>;

  // Conditions, then readiness, then the action. The chips sit against the
  // button so the customer confirms what they are about to allow in one glance
  // instead of pairing a checklist with a button a section away.
  const joinFlow = (
    <div className="md:bottom-0 md:z-10 md:sticky flex flex-col gap-3 md:bg-white/95 md:dark:bg-gray-900/95 md:backdrop-blur md:pt-4 md:pb-6">
      <h2 className="font-bold text-gray-900 dark:text-gray-100 paragraph-2">{isReady ? "Ready to join" : "Almost ready"}</h2>
      <ul className="flex flex-wrap gap-2">
        <DevicePermissionChip hasPermission={permissions.video} permission={"video"} requestPermissions={requestPermissions} />
        <DevicePermissionChip hasPermission={permissions.audio} permission={"audio"} requestPermissions={requestPermissions} />
        {tracksLocation && <LocationPermissionChip {...location} />}
        {tracksHeading && <CompassPermissionChip {...compass} />}
      </ul>
      {microphoneWarning}
      {cameraWarning}
      {/* On a phone the same button rides along the bottom of the screen. */}
      <div className="hidden md:block">{joinButton}</div>
    </div>
  );

  return (
    <div className="box-border relative flex flex-col items-center bg-white dark:bg-gray-900 px-5 md:px-10 lg:px-12 pt-6 w-full h-max min-h-screen font-base text-gray-900 dark:text-gray-100 leading-normal">
      {/* One centred column holds the page. Margin auto rather than
          justify-center, so a lobby taller than the window scrolls from the top
          instead of losing its head. */}
      <div className="md:my-auto w-full max-w-6xl">
        <section className="gap-10 lg:gap-14 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-9 xl:grid-cols-2 pb-8 w-full">
          {/* The preview stays put while the notice and the checklist scroll
              past it: on a short window it would otherwise be the first thing
              to leave the screen, and it is what the customer came to check. */}
          <div className="hidden md:top-6 md:sticky md:flex md:flex-col md:self-start md:col-span-2 lg:col-span-5 xl:col-span-1">
            <LobbyVideo videoStream={videoStream} />
          </div>

          <div className="flex flex-col gap-7 md:col-span-1 lg:col-span-4 xl:col-span-1 w-full">
            <header className="flex flex-col gap-6">
              {/* Without this the flex row stretches the SVG to the column and
                  the wordmark centres itself inside its own viewBox. */}
              <Logo className="self-start w-auto shrink-0" size="medium" />
              <div className="flex flex-col gap-1">
                <h1 className="text-gray-900 dark:text-gray-100 heading-3">Live Video Estimate</h1>
                {startTime && <p className="paragraph-1">{startTime}</p>}
                {meetingWith && <p className={`paragraph-2 ${MUTED}`}>with {meetingWith}</p>}
              </div>
            </header>

            <div className="md:hidden">
              <LobbyVideo videoStream={videoStream} />
            </div>

            {isClient && <ConsentNotice />}

            {joinFlow}

            {/* Reference material once the decision is made, so it carries no
                card of its own — a rule and a label are enough. */}
            <section className="flex flex-col gap-2 pt-6 border-gray-200 dark:border-gray-800 border-t">
              <h2 className={SECTION_LABEL}>Appointment</h2>
              <div className="flex flex-col gap-1">
                <p className="paragraph-2">{address}</p>
                {!isClient && customerName && <p className={`paragraph-3 ${MUTED}`}>Customer · {customerName}</p>}
                {staffName && <p className={`paragraph-3 ${MUTED}`}>Sales rep · {staffName}</p>}
              </div>
            </section>
          </div>
        </section>
      </div>

      {/* On a phone the notice and the checklist push the only button that
          matters past the fold, so it rides along the bottom instead. */}
      <div className="md:hidden bottom-0 z-10 sticky self-stretch bg-white/95 dark:bg-gray-900/95 backdrop-blur -mx-5 mt-auto p-5 border-gray-200 dark:border-gray-800 border-t">{joinButton}</div>
    </div>
  );
}
